import './CustomDropdown.css';
import { useState, useRef, useEffect } from 'react';
import { HiOutlineChevronDown } from 'react-icons/hi';
import { FiCheck } from 'react-icons/fi';
import Tooltip from '../Tooltip/Tooltip';

interface CustomDropdownProps {
  value: string;
  options: (string | { label: string; value: string; tooltip?: string })[];
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  placeholder?: string;
}

function CustomDropdown({ value, options, onChange, label, className = '', placeholder }: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getOptionValue = (option: string | { label: string; value: string }): string => {
    return typeof option === 'string' ? option : option.value;
  };

  const getOptionLabel = (option: string | { label: string; value: string; tooltip?: string }): string => {
    return typeof option === 'string' ? option : option.label;
  };

  const getOptionTooltip = (option: string | { label: string; value: string; tooltip?: string }): string | undefined => {
    return typeof option === 'string' ? undefined : option.tooltip;
  };

  const handleSelect = (option: string | { label: string; value: string }) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
  };

  return (
    <div className={`custom-dropdown-wrapper ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className="custom-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={!value && placeholder ? 'custom-dropdown-placeholder' : ''}>{(value && options.length > 0 ? (getOptionLabel(options.find(o => getOptionValue(o) === value) || value)) : value) || placeholder || ''}</span>
        <HiOutlineChevronDown className={`dropdown-icon ${isOpen ? 'open' : ''}`} />
      </button>
      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((option) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const optionTooltip = getOptionTooltip(option);
            const itemButton = (
              <button
                type="button"
                key={optionValue}
                className={`custom-dropdown-item ${value === optionValue ? 'active' : ''}`}
                onClick={() => handleSelect(option)}
              >
                <span className="custom-dropdown-item-label">{optionLabel}</span>
                {value === optionValue && <FiCheck className="dropdown-check-icon" />}
              </button>
            );

            return optionTooltip ? (
              <Tooltip key={optionValue} content={optionTooltip} side="right">
                {itemButton}
              </Tooltip>
            ) : itemButton;
          })}
        </div>
      )}
    </div>
  );
}

export default CustomDropdown;
